// 選択中オブジェクトに対して 8 方位リサイズハンドルと 1 つの回転ハンドルを描画する。
//
// 設計:
// - 選択中の object の AABB（rotation を無視した world-space 矩形）にハンドルを配置する。
//   rotation を持つ object でも、ハンドル自体は回転しない簡素なバウンディングボックスを表示する。
// - 各ハンドルは独自の pointer 操作（リサイズ / 回転）を行うため、それぞれ
//   stopPropagation で親方向のドラッグや stage のパンへ波及しないようにする。
// - プレビューは対象オブジェクトの DOM 要素の left/top/width/height/transform を
//   直接書き換える（doc store / サーバ同期はせず、pointerup で最終値だけ送信）。
//
// 既知の制約:
// - 1 件のみ選択時に表示（複数選択への対応は別フェーズ）
// - rotation 状態の object でも world 軸でリサイズする（object-local リサイズは非対応）

import { useLayoutEffect, useRef, useState } from "react";
import type { CanvasObject } from "@refinement-canvas/domain";
import { useCanvasStore } from "./store/canvas-store";
import { useCanvasDocStore } from "./store/canvas-doc-store";
import { useCanvasOpDispatcherContext } from "./dispatcher-context";
import { useResizeHandle, type ResizeDirection } from "./input/use-resize-handle";
import { useRotationHandle } from "./input/use-rotation-handle";

const HANDLE_SIZE = 10;
const ROTATION_HANDLE_OFFSET = 28;

export function CanvasSelectionOverlay(): JSX.Element | null {
  const selectedIds = useCanvasStore((s) => s.selectedObjectIds);
  const objects = useCanvasDocStore((s) => s.objects);

  if (selectedIds.length !== 1) return null;
  const id = selectedIds[0]!;
  const target = objects[id];
  if (!target) return null;
  // ペンストロークはリサイズ/回転対象外（線の太さや形状を後から変更しない設計）
  if (target.type === "pen-stroke") return null;

  return <SingleSelectionOverlay key={id} object={target} />;
}

interface SingleProps {
  readonly object: Exclude<CanvasObject, { type: "pen-stroke" }>;
}

function SingleSelectionOverlay({ object }: SingleProps): JSX.Element {
  const dispatcher = useCanvasOpDispatcherContext();
  // 対象オブジェクトの DOM 要素をテストID で取得し、プレビュー時に直接書き換える。
  //
  // ビューポートカリングでオブジェクトがアンマウント→再マウントされたとき、
  // 古い DOM を握ったままだとリサイズ/回転プレビューが効かなくなる。
  // そのため毎コミットの useLayoutEffect で querySelector を再実行し、
  // 直近の DOM 要素を取り直すようにしている（initial mount の取得遅延も解消）。
  const targetRef = useRef<HTMLElement | null>(null);
  const [, forceTick] = useState(0);

  useLayoutEffect(() => {
    const el = document.querySelector<HTMLElement>(`[data-testid="canvas-object-${object.id}"]`);
    if (targetRef.current !== el) {
      targetRef.current = el ?? null;
      // 取得直後に再描画（ハンドルが対象に追従するよう）
      forceTick((n) => n + 1);
    }
  });

  // 8 リサイズハンドル分の ref
  const handleRefs: Record<ResizeDirection, React.RefObject<HTMLDivElement | null>> = {
    nw: useRef<HTMLDivElement | null>(null),
    n: useRef<HTMLDivElement | null>(null),
    ne: useRef<HTMLDivElement | null>(null),
    e: useRef<HTMLDivElement | null>(null),
    se: useRef<HTMLDivElement | null>(null),
    s: useRef<HTMLDivElement | null>(null),
    sw: useRef<HTMLDivElement | null>(null),
    w: useRef<HTMLDivElement | null>(null),
  };
  const rotationHandleRef = useRef<HTMLDivElement | null>(null);

  // 8 ハンドル分のドラッグハンドラを設定
  // useResizeHandle は内部で useEffect を使うので Hook の数を一定にする必要がある。
  useResizeHandle({
    direction: "nw",
    handleRef: handleRefs.nw,
    objectId: object.id,
    dispatcher,
    initialX: object.x,
    initialY: object.y,
    initialW: object.w,
    initialH: object.h,
    targetRef,
  });
  useResizeHandle({
    direction: "n",
    handleRef: handleRefs.n,
    objectId: object.id,
    dispatcher,
    initialX: object.x,
    initialY: object.y,
    initialW: object.w,
    initialH: object.h,
    targetRef,
  });
  useResizeHandle({
    direction: "ne",
    handleRef: handleRefs.ne,
    objectId: object.id,
    dispatcher,
    initialX: object.x,
    initialY: object.y,
    initialW: object.w,
    initialH: object.h,
    targetRef,
  });
  useResizeHandle({
    direction: "e",
    handleRef: handleRefs.e,
    objectId: object.id,
    dispatcher,
    initialX: object.x,
    initialY: object.y,
    initialW: object.w,
    initialH: object.h,
    targetRef,
  });
  useResizeHandle({
    direction: "se",
    handleRef: handleRefs.se,
    objectId: object.id,
    dispatcher,
    initialX: object.x,
    initialY: object.y,
    initialW: object.w,
    initialH: object.h,
    targetRef,
  });
  useResizeHandle({
    direction: "s",
    handleRef: handleRefs.s,
    objectId: object.id,
    dispatcher,
    initialX: object.x,
    initialY: object.y,
    initialW: object.w,
    initialH: object.h,
    targetRef,
  });
  useResizeHandle({
    direction: "sw",
    handleRef: handleRefs.sw,
    objectId: object.id,
    dispatcher,
    initialX: object.x,
    initialY: object.y,
    initialW: object.w,
    initialH: object.h,
    targetRef,
  });
  useResizeHandle({
    direction: "w",
    handleRef: handleRefs.w,
    objectId: object.id,
    dispatcher,
    initialX: object.x,
    initialY: object.y,
    initialW: object.w,
    initialH: object.h,
    targetRef,
  });
  useRotationHandle({
    handleRef: rotationHandleRef,
    objectId: object.id,
    dispatcher,
    centerX: object.x + object.w / 2,
    centerY: object.y + object.h / 2,
    initialRotation: object.rotation ?? 0,
    targetRef,
  });

  // ハンドル位置（ステージ座標系）
  const left = object.x;
  const top = object.y;
  const right = object.x + object.w;
  const bottom = object.y + object.h;
  const cx = (left + right) / 2;
  const cy = (top + bottom) / 2;

  const handlePositions: { dir: ResizeDirection; x: number; y: number; cursor: string }[] = [
    { dir: "nw", x: left, y: top, cursor: "nwse-resize" },
    { dir: "n", x: cx, y: top, cursor: "ns-resize" },
    { dir: "ne", x: right, y: top, cursor: "nesw-resize" },
    { dir: "e", x: right, y: cy, cursor: "ew-resize" },
    { dir: "se", x: right, y: bottom, cursor: "nwse-resize" },
    { dir: "s", x: cx, y: bottom, cursor: "ns-resize" },
    { dir: "sw", x: left, y: bottom, cursor: "nesw-resize" },
    { dir: "w", x: left, y: cy, cursor: "ew-resize" },
  ];

  return (
    <div
      data-testid="canvas-selection-overlay"
      style={{
        position: "absolute",
        inset: 0,
        pointerEvents: "none",
        zIndex: Number.MAX_SAFE_INTEGER,
      }}
    >
      {handlePositions.map((p) => (
        <div
          key={p.dir}
          ref={handleRefs[p.dir]}
          data-testid={`selection-handle-${p.dir}`}
          style={{
            position: "absolute",
            left: `${p.x - HANDLE_SIZE / 2}px`,
            top: `${p.y - HANDLE_SIZE / 2}px`,
            width: `${HANDLE_SIZE}px`,
            height: `${HANDLE_SIZE}px`,
            background: "var(--color-surface)",
            border: "2px solid var(--color-accent)",
            borderRadius: "2px",
            cursor: p.cursor,
            pointerEvents: "auto",
            boxSizing: "border-box",
          }}
        />
      ))}
      <div
        ref={rotationHandleRef}
        data-testid="selection-handle-rotation"
        style={{
          position: "absolute",
          left: `${cx - HANDLE_SIZE / 2}px`,
          top: `${top - ROTATION_HANDLE_OFFSET - HANDLE_SIZE / 2}px`,
          width: `${HANDLE_SIZE}px`,
          height: `${HANDLE_SIZE}px`,
          background: "var(--color-accent)",
          border: "2px solid var(--color-surface)",
          borderRadius: "50%",
          cursor: "grab",
          pointerEvents: "auto",
          boxSizing: "border-box",
        }}
        title="ドラッグで回転（Shift で 15° スナップ）"
      />
    </div>
  );
}
